"use client";

import Script from "next/script";
import { useEffect } from "react";
import { analyticsEnabled, getAnalyticsEnv } from "../lib/env";
import { setAnalyticsContext, type AnalyticsContext } from "../lib/analytics";

type AnalyticsSurface = "landing" | "admin";

type Props = AnalyticsContext & { surface?: AnalyticsSurface };

export function AnalyticsScripts({
  surface = "landing",
  tenantId,
  landingId,
  userId,
}: Props) {
  const env = getAnalyticsEnv();
  const gaMeasurementId =
    surface === "landing" ? env.gaMeasurementId : env.adminGaMeasurementId;
  const clarityProjectId =
    surface === "landing" ? env.clarityProjectId : env.adminClarityProjectId;

  useEffect(() => {
    setAnalyticsContext({ tenantId, landingId, userId });

    if (typeof window === "undefined") return;

    if (typeof window.gtag === "function" && gaMeasurementId) {
      if (userId) window.gtag("config", gaMeasurementId, { user_id: userId });
      const properties: Record<string, string> = {};
      if (tenantId) properties.tenant_id = tenantId;
      if (landingId) properties.landing_id = landingId;
      if (Object.keys(properties).length > 0) {
        window.gtag("set", "user_properties", properties);
      }
    }

    if (typeof window.clarity === "function") {
      if (tenantId) window.clarity("set", "tenant_id", tenantId);
      if (landingId) window.clarity("set", "landing_id", landingId);
      if (userId) window.clarity("set", "user_id", userId);
    }
  }, [gaMeasurementId, tenantId, landingId, userId]);

  if (!analyticsEnabled) return null;

  return (
    <>
      {gaMeasurementId && (
        <>
          <Script
            id="ga4-src"
            strategy="afterInteractive"
            src={`https://www.googletagmanager.com/gtag/js?id=${gaMeasurementId}`}
          />
          <Script id="ga4-init" strategy="afterInteractive">
            {`window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}window.gtag=gtag;gtag('js',new Date());gtag('config','${gaMeasurementId}');`}
          </Script>
        </>
      )}
      {clarityProjectId && (
        <Script id="clarity" strategy="afterInteractive">
          {`(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window, document, "clarity", "script", "${clarityProjectId}");`}
        </Script>
      )}
    </>
  );
}
